import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components';
import { useEffect, useState } from 'react';

import { MissingDefaultHint } from './components/MissingDefaultHint';

import './components/Section.scss';

const LEI_REGEX = /^[A-Z0-9]{20}$/;
export const isValidLei = (value: string): boolean => LEI_REGEX.test(value);

interface GeneralInformationSectionProps {
  entity: string | null | undefined;
  publisherUrl: string | null | undefined;
  publisherCountry: string | undefined;
  packageName: string | null | undefined;
  leiMissing: boolean;
  batchedUpdate: (patch: { entity?: string | null; publisherUrl?: string | null; publisherCountry?: string; packageName?: string | null }) => void;
}

export const GeneralInformationSection = ({
  entity,
  publisherUrl,
  publisherCountry,
  packageName,
  leiMissing,
  batchedUpdate,
}: GeneralInformationSectionProps) => {
  const { t } = useTranslation('tag');

  const [localEntity, setLocalEntity] = useState(entity ?? '');
  const [entityError, setEntityError] = useState(false);
  const [localUrl, setLocalUrl] = useState(publisherUrl ?? '');
  const [localCountry, setLocalCountry] = useState(publisherCountry ?? 'SE');
  const [localName, setLocalName] = useState(packageName ?? '');

  useEffect(() => setLocalEntity(entity ?? ''), [entity]);
  useEffect(() => setLocalUrl(publisherUrl ?? ''), [publisherUrl]);
  useEffect(() => setLocalCountry(publisherCountry ?? 'SE'), [publisherCountry]);
  useEffect(() => setLocalName(packageName ?? ''), [packageName]);

  return (
    <section className="defaults-section">
      <div className="defaults-section__header">
        <div className="defaults-section__title">
          <Typography variant="body-8" fontWeight="semibold">
            {t('taxonomy.general.label')}
          </Typography>
          {leiMissing && <MissingDefaultHint labelKey="defaults.missing.section" />}
        </div>
      </div>

      <div className="general-info-grid">
        <div className="general-info-field">
          <div className="general-info-field__label mb-2">
            <Typography variant="body-7" fontWeight="semibold">
              {t('company.label')}
            </Typography>
            {leiMissing && <MissingDefaultHint labelKey="defaults.missing.lei" />}
          </div>
          <input
            className="tagging-settings__input tagging-settings__input--large"
            type="text"
            value={localEntity}
            maxLength={20}
            placeholder="20-char LEI (ISO 17442)"
            aria-invalid={entityError}
            onChange={(e) => {
              const v = e.target.value.toUpperCase().replace(/\s+/g, '');
              setLocalEntity(v);
              if (v === '') {
                setEntityError(false);
                batchedUpdate({ entity: null });
              } else if (isValidLei(v)) {
                setEntityError(false);
                batchedUpdate({ entity: v });
              } else {
                setEntityError(true);
              }
            }}
          />
          {entityError && (
            <Typography variant="body-7" className="general-info-field__error">
              {t('company.lei.invalid')}
            </Typography>
          )}
        </div>

        <div className="general-info-field">
          <Typography variant="body-7" fontWeight="semibold" className="mb-2">
            {t('taxonomy.publisher.url')}
          </Typography>
          <input
            className="tagging-settings__input tagging-settings__input--large"
            type="text"
            value={localUrl}
            placeholder="www.example.com"
            onChange={(e) => {
              setLocalUrl(e.target.value);
              batchedUpdate({ publisherUrl: e.target.value || null });
            }}
          />
        </div>

        <div className="general-info-field">
          <Typography variant="body-7" fontWeight="semibold" className="mb-2">
            {t('taxonomy.publisher.country')}
          </Typography>
          <input
            className="tagging-settings__input tagging-settings__input--large general-info-field__input--narrow"
            type="text"
            value={localCountry}
            maxLength={2}
            placeholder="SE"
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              setLocalCountry(v);
              if (v.length === 2) batchedUpdate({ publisherCountry: v });
            }}
          />
        </div>

        <div className="general-info-field">
          <Typography variant="body-7" fontWeight="semibold" className="mb-2">
            {t('taxonomy.package.name')}
          </Typography>
          <input
            className="tagging-settings__input tagging-settings__input--large"
            type="text"
            value={localName}
            placeholder="Annual Report 2024"
            onChange={(e) => {
              setLocalName(e.target.value);
              batchedUpdate({ packageName: e.target.value || null });
            }}
          />
        </div>
      </div>
    </section>
  );
};
